import {
  CalendarIcon,
  ChevronUpDownIcon,
  ListBulletIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { api } from "@/lib/api";
import { useProject } from "@/lib/project";
import { keys } from "@/lib/queries";
import type { EntityType, Property, PropertyType, Values } from "@/lib/types";
import { cn, formatDay } from "@/lib/utils";
import { Tag } from "../ui";
import { useFeedback } from "../ui/feedback";
import { MenuDivider, MenuItem, Popover } from "../ui/Popover";
import { SelectEditor } from "./SelectEditor";

export const propertyTypeIcons: Record<PropertyType, React.ComponentType<{ className?: string }>> = {
  select: ChevronUpDownIcon,
  multi_select: ListBulletIcon,
  date: CalendarIcon,
};

export const propertyTypeLabels: Record<PropertyType, string> = {
  select: "Select",
  multi_select: "Multi-select",
  date: "Date",
};

/** Two-column row used for both built-in fields and custom properties. */
export function PropertyRow({
  icon: Icon,
  label,
  children,
  labelSlot,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: ReactNode;
  children: ReactNode;
  labelSlot?: (content: ReactNode) => ReactNode;
}) {
  const content = (
    <>
      <Icon className="size-4 shrink-0 opacity-80" />
      <span className="truncate">{label}</span>
    </>
  );
  return (
    <div className="flex min-h-[34px] items-start gap-1">
      <div className="w-36 shrink-0 sm:w-48">
        {labelSlot ? (
          labelSlot(content)
        ) : (
          <div className="flex h-[34px] items-center gap-2 px-1.5 text-sm text-muted">{content}</div>
        )}
      </div>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

/** Clickable value area with hover background; shows "Empty" when there's nothing. */
export function ValueButton({
  children,
  empty,
  disabled,
  className,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { empty?: boolean; ref?: React.Ref<HTMLButtonElement> }) {
  return (
    <button
      type="button"
      disabled={disabled}
      className={cn(
        "flex min-h-[34px] w-full flex-wrap items-center gap-1 rounded-md px-2 py-1 text-start text-sm",
        !disabled && "hover:bg-hover",
        className,
      )}
      {...rest}
    >
      {empty ? <span className="text-faint">Empty</span> : children}
    </button>
  );
}

export function PropertyValueView({ property, value }: { property: Property; value: string[] }) {
  if (property.type === "date") return <span>{formatDay(value[0])}</span>;
  const byId = new Map(property.options.map((o) => [o.id, o]));
  const sorted = value
    .map((id) => byId.get(id))
    .filter((o) => !!o)
    .sort((a, b) => a.position - b.position);
  return (
    <>
      {sorted.map((o) => (
        <Tag key={o.id} color={o.color}>
          {o.label}
        </Tag>
      ))}
    </>
  );
}

/** Keeps a local optimistic copy of a property value while the change is saved. */
export function usePropertyValue(entityId: string, propertyId: string, serverValue: string[]) {
  const { pid } = useProject();
  const qc = useQueryClient();
  const [local, setLocal] = useState<string[] | null>(null);
  const set = async (next: string[]) => {
    setLocal(next);
    try {
      await api.setValue(pid, entityId, propertyId, next);
    } catch (err) {
      window.dispatchEvent(new CustomEvent("app:error", { detail: err }));
    }
    await qc.invalidateQueries({ queryKey: keys.project(pid) });
    setLocal(null);
  };
  return [local ?? serverValue, set] as const;
}

export function DateEditor({ value, onChange, onClose }: { value: string | undefined; onChange: (v: string | null) => void; onClose: () => void }) {
  const [v, setV] = useState(value ?? "");
  return (
    <div className="w-64 space-y-2 p-2">
      <input
        type="date"
        autoFocus
        value={v}
        onChange={(e) => {
          setV(e.target.value);
          if (e.target.value) onChange(e.target.value);
        }}
        onKeyDown={(e) => e.key === "Enter" && onClose()}
        className="field"
      />
      {value && (
        <button
          type="button"
          onClick={() => {
            onChange(null);
            onClose();
          }}
          className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-muted hover:bg-hover"
        >
          <XMarkIcon className="size-4" /> Clear date
        </button>
      )}
    </div>
  );
}

/** Editable value for one custom property. `variant="cell"` is used inside tables. */
export function PropertyValue({
  property,
  entityId,
  values,
  variant = "row",
}: {
  property: Property;
  entityId: string;
  values: Values;
  variant?: "row" | "cell";
}) {
  const { canEdit } = useProject();
  const [value, setValue] = usePropertyValue(entityId, property.id, values[property.id] ?? []);
  const empty = value.length === 0;
  return (
    <Popover
      disabled={!canEdit}
      trigger={(t) => (
        <ValueButton
          ref={t.ref}
          onClick={t.onClick}
          empty={empty && variant === "row"}
          disabled={!canEdit}
          className={cn(variant === "cell" && "min-h-[36px] flex-nowrap overflow-hidden rounded-none", t.open && "bg-hover")}
        >
          <PropertyValueView property={property} value={value} />
        </ValueButton>
      )}
    >
      {(close) =>
        property.type === "date" ? (
          <DateEditor value={value[0]} onChange={(v) => setValue(v ? [v] : [])} onClose={close} />
        ) : (
          <SelectEditor
            propertyId={property.id}
            options={property.options}
            value={value}
            multi={property.type === "multi_select"}
            onChange={setValue}
            onClose={close}
            canManage={canEdit}
          />
        )
      }
    </Popover>
  );
}

function PropertyNameMenu({ property, content }: { property: Property; content: ReactNode }) {
  const { pid, canEdit } = useProject();
  const qc = useQueryClient();
  const { confirm } = useFeedback();
  const [name, setName] = useState(property.name);
  useEffect(() => setName(property.name), [property.name]);
  const refresh = () => qc.invalidateQueries({ queryKey: keys.project(pid) });

  const rename = async () => {
    const v = name.trim();
    if (!v || v === property.name) return;
    try {
      await api.updateProperty(pid, property.id, v);
    } catch (err) {
      window.dispatchEvent(new CustomEvent("app:error", { detail: err }));
    }
    refresh();
  };

  if (!canEdit) return <div className="flex h-[34px] items-center gap-2 px-1.5 text-sm text-muted">{content}</div>;

  return (
    <Popover
      className="w-60 p-1"
      onOpenChange={(open) => !open && rename()}
      trigger={(t) => (
        <button
          ref={t.ref}
          onClick={t.onClick}
          type="button"
          className={cn("flex h-[34px] w-full items-center gap-2 rounded-md px-1.5 text-sm text-muted hover:bg-hover", t.open && "bg-hover")}
        >
          {content}
        </button>
      )}
    >
      {(close) => (
        <>
          <div className="p-1">
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && close()}
              className="field py-1"
              dir="auto"
            />
          </div>
          <div className="px-2 py-1 text-xs text-faint">
            Type: {propertyTypeLabels[property.type]} · shared by every {property.entityType} in this project
          </div>
          <MenuDivider />
          <MenuItem
            icon={TrashIcon}
            danger
            onClick={async () => {
              close();
              const ok = await confirm({
                title: `Delete the "${property.name}" property?`,
                message: `It will be removed from every ${property.entityType} in this project, along with its values.`,
                confirmLabel: "Delete property",
                danger: true,
              });
              if (!ok) return;
              try {
                await api.deleteProperty(pid, property.id);
              } catch (err) {
                window.dispatchEvent(new CustomEvent("app:error", { detail: err }));
              }
              refresh();
            }}
          >
            Delete property
          </MenuItem>
        </>
      )}
    </Popover>
  );
}

export function CustomPropertyRows({ entityType, entityId, values }: { entityType: EntityType; entityId: string; values: Values }) {
  const { propsFor } = useProject();
  return (
    <>
      {propsFor(entityType).map((p) => (
        <PropertyRow
          key={p.id}
          icon={propertyTypeIcons[p.type]}
          label={p.name}
          labelSlot={(content) => <PropertyNameMenu property={p} content={content} />}
        >
          <PropertyValue property={p} entityId={entityId} values={values} />
        </PropertyRow>
      ))}
    </>
  );
}

export function AddPropertyButton({ entityType }: { entityType: EntityType }) {
  const { pid, canEdit } = useProject();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [type, setType] = useState<PropertyType>("select");
  const [saving, setSaving] = useState(false);
  if (!canEdit) return null;

  const create = async (close: () => void) => {
    const n = name.trim();
    if (!n) return;
    setSaving(true);
    try {
      await api.createProperty(pid, { entityType, name: n, type });
      await qc.invalidateQueries({ queryKey: keys.bundle(pid) });
      setName("");
      close();
    } catch (err) {
      window.dispatchEvent(new CustomEvent("app:error", { detail: err }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover
      className="w-64 p-2"
      trigger={(t) => (
        <button
          ref={t.ref}
          onClick={t.onClick}
          type="button"
          className="flex h-[34px] items-center gap-2 rounded-md px-1.5 text-sm text-faint hover:bg-hover hover:text-muted"
        >
          <PlusIcon className="size-4" /> Add a property
        </button>
      )}
    >
      {(close) => (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            create(close);
          }}
          className="space-y-2"
        >
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Property name" className="field" dir="auto" />
          <div className="space-y-0.5">
            {(Object.keys(propertyTypeLabels) as PropertyType[]).map((t) => {
              const Icon = propertyTypeIcons[t];
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setType(t)}
                  className={cn("flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-hover", type === t && "bg-active")}
                >
                  <Icon className="size-4 opacity-70" />
                  {propertyTypeLabels[t]}
                </button>
              );
            })}
          </div>
          <p className="px-1 text-xs text-faint">
            {type === "date" ? "Pick a date for each item." : "Add values from the property cell. Every " + entityType + " in the project gets this property."}
          </p>
          <button
            type="submit"
            disabled={!name.trim() || saving}
            className="flex h-8 w-full items-center justify-center gap-1.5 rounded-md bg-accent text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
          >
            <PencilIcon className="size-3.5" /> Create property
          </button>
        </form>
      )}
    </Popover>
  );
}
